import React, { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { ACCEPT, clientValidate, errMsg } from "../../lib/portfolio";

export function MediaUploader({ projectId, onUploaded, disabled }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);

  const upload = async (files) => {
    for (const file of Array.from(files)) {
      const problem = clientValidate(file);
      if (problem) { toast.error(`${file.name}: ${problem}`); continue; }
      try {
        setProgress({ name: file.name, pct: 0 });
        await api.post(`/admin/portfolio/projects/${projectId}/media`, file, {
          params: { filename: file.name },
          headers: { "Content-Type": file.type || "application/octet-stream" },
          onUploadProgress: (e) => e.total && setProgress({ name: file.name, pct: Math.round((e.loaded / e.total) * 100) }),
        });
        toast.success(`${file.name} uploaded.`);
        onUploaded?.();
      } catch (e) {
        toast.error(`${file.name}: ${errMsg(e, "Upload failed.")}`);
      }
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) upload(e.dataTransfer.files); }}
      className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-erf-gold bg-erf-creamSecondary" : "border-erf-borderCream bg-white"}`}
      data-testid="portfolio-media-dropzone"
    >
      <UploadCloud className="w-8 h-8 mx-auto text-erf-goldHover" aria-hidden="true" />
      <p className="mt-2 text-sm font-nav text-erf-dark">Drag photos or videos here, or choose files.</p>
      <p className="text-xs font-nav text-erf-dark/70">JPEG · PNG · WebP · HEIC up to 50 MB — MP4 · MOV · M4V · WebM up to 500 MB.</p>
      <input ref={inputRef} type="file" multiple accept={ACCEPT} className="sr-only" id="portfolio-file-input"
        onChange={(e) => upload(e.target.files)} disabled={disabled} data-testid="portfolio-media-file-input" />
      <Button type="button" variant="outline" className="mt-3 border-erf-borderCream text-erf-dark"
        onClick={() => inputRef.current?.click()} disabled={disabled || !!progress} data-testid="portfolio-media-choose-btn">
        Choose files
      </Button>
      {progress && (
        <div className="mt-3 text-xs font-mono text-erf-dark" role="status" data-testid="portfolio-upload-progress">
          Uploading {progress.name} — {progress.pct}%
        </div>
      )}
    </div>
  );
}
